import { Component, OnInit, ViewChild, ElementRef, AfterViewChecked } from '@angular/core';
// import { Router } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { ChatbotService, ChatMessage, ChatRequest, VisualizationPayload } from '../../services/chatbot.service';

interface MessageContentPart {
  type: 'text' | 'table';
  text?: string;
  headers?: string[];
  rows?: string[][];
}

@Component({
  selector: 'app-chatbot',
  templateUrl: './chatbot.component.html',
  styleUrls: ['./chatbot.component.scss'],
  standalone: false,
})
export class ChatbotComponent implements OnInit, AfterViewChecked {

  @ViewChild('messagesContainer') messagesContainer!: ElementRef;
  @ViewChild('queryInput') queryInput!: ElementRef;

  isOpen: boolean = false;
  isLoading: boolean = false;
  isFullscreen: boolean = false;
  userQuery: string = '';
  userName: string = '';
  messages: ChatMessage[] = [];
  private shouldScrollToBottom: boolean = false;
  private messagePartsCache = new WeakMap<ChatMessage, MessageContentPart[]>();

  // Predefined questions ordered by difficulty (Easy → Advanced)
  showPredefinedQuestions: boolean = false;
  isPredefinedExpanded: boolean = false;
  
   // Context window usage (Copilot-style)
  maxContextWindow = 128000; // 128K tokens for GPT-5.1
  contextWindowUsed = 0;
  contextWindowBreakdown = {
    prompt: 0,
    completion: 0,
    reserved: 128000
  };
  // Collapsible context panel state
  contextPanelCollapsed = true;

  // Track planner agent token usage for visual bar
  plannerTokenUsed = 0;

  predefinedQuestions: string[] = [
    'What are all valid development areas in the system?',
    'List all CTB facilities in Bexar',
    'Which dev areas share facilities?',
    'Identify facilities serving multiple dev areas.',
    'Which dev areas have wells without mapped facilities?',
    'Who owns the Bexar development area?',
    'What is the total oil capacity for all CTB facilities in Bexar?',
  ];

  // Quick action buttons for initial greeting
  quickActions: { label: string; query: string }[] = [];

  constructor(
    private chatbotService: ChatbotService,
    // private router: Router,
    private msalService: MsalService
  ) {}

  ngOnInit(): void {
    const account = this.msalService.instance.getActiveAccount();
    this.userName = account?.name?.split(' ')[0] || account?.username?.split('@')[0] || 'there';
  }

  ngAfterViewChecked(): void {
    if (this.shouldScrollToBottom) {
      this.scrollToBottom();
      this.shouldScrollToBottom = false;
    }
  }

  // ─── PANEL TOGGLE ────────────────────────────────────────────────────────────
  togglePanel(): void {
    this.isOpen = !this.isOpen;
    if (this.isOpen && this.messages.length === 0) {
      this.showGreeting();
      this.isPredefinedExpanded = false; // Collapse predefined questions by default
    }
    if (this.isOpen) {
      setTimeout(() => this.queryInput?.nativeElement?.focus(), 300);
    }
  }

  closePanel(): void {
    this.isOpen = false;
    this.isFullscreen = false;
  }

  toggleFullscreen(): void {
    this.isFullscreen = !this.isFullscreen;
  }

  // ─── GREETING ────────────────────────────────────────────────────────────────
  private showGreeting(): void {
    this.quickActions = [];
  
    this.messages.push({
      role: 'assistant',
      content: `Hello, ${this.userName}! 👋 I'm CMP GENIE, your Data Intelligence Assistant.\n\nI can help you with:\n• FE Capacities & Utilization\n• Flow Networks & Routing\n• Facility Information\n• Analytics & Insights \n\nSelect a question below to get started, or type your own question!`,
      timestamp: new Date(),
      followUpSuggestions: []
    });
    this.shouldScrollToBottom = true;
    
    // Show predefined questions after a short delay for better UX
    setTimeout(() => {
      this.showPredefinedQuestions = true;
      this.isPredefinedExpanded = true;
      this.shouldScrollToBottom = true;
    }, 300);
  }

  // ─── SEND MESSAGE ─────────────────────────────────────────────────────────────
  async sendMessage(queryOverride?: string): Promise<void> {
    const query = (queryOverride || this.userQuery).trim();
    if (!query || this.isLoading) return;

    // Add user message
    this.messages.push({
      role: 'user',
      content: query,
      timestamp: new Date()
    });

    // Clear input
    this.userQuery = '';
    this.isLoading = true;
    this.shouldScrollToBottom = true;


    // Add loading bubble
    const loadingMsg: ChatMessage = {
      role: 'assistant',
      content: '',
      timestamp: new Date(),
      isLoading: true
    };
    this.messages.push(loadingMsg);
    this.shouldScrollToBottom = true;

    try {
      const history = this.messages
        .filter(m => !m.isLoading && m.role !== 'assistant' || (m.role === 'assistant' && m.content))
        .slice(-10)
        .map(m => ({ role: m.role, content: m.content }));

      const request: ChatRequest = {
        query,
        // module: this.detectModule(),
        conversationHistory: history,
        // currentRoute: this.router.url
      };

      const response = await this.chatbotService.sendMessage(request);

      // Remove loading bubble
      this.messages = this.messages.filter(m => !m.isLoading);

      if (response.clarificationNeeded) {
        this.messages.push({
          role: 'assistant',
          content: response.clarificationQuestion || 'Can you clarify your request?',
          timestamp: new Date(),
          followUpSuggestions: response.clarificationOptions || []
        });
      } else {
        // Auto-detect structured data and enhance visualization if needed
        const enhancedVisualization = this.enhanceVisualizationFromText(
          response.answer || '',
          response.visualization as VisualizationPayload
        );
        
        this.messages.push({
          role: 'assistant',
          content: response.answer || 'No data found for your query.',
          warnings: response.warnings || [],
          insights: response.insights || [],
          followUpSuggestions: response.followUpSuggestions || [],
          visualization: enhancedVisualization,
          toolsUsed: response.toolsUsed || [],
          timestamp: new Date(),
          plannerTokenUsage: response.plannerTokenUsage,
          answerTokenUsage: response.answerTokenUsage
        });

        // Update context window usage bar (Copilot-style)
        // Track planner agent usage
        if (response.plannerTokenUsage) {
          const plannerPrompt = response.plannerTokenUsage.prompt_tokens || 0;
          const plannerCompletion = response.plannerTokenUsage.completion_tokens || 0;
          this.plannerTokenUsed = plannerPrompt + plannerCompletion;
        } else {
          this.plannerTokenUsed = 0;
        }
        if (response.answerTokenUsage) {
          const prompt = response.answerTokenUsage.prompt_tokens || 0;
          const completion = response.answerTokenUsage.completion_tokens || 0;
          const total = response.answerTokenUsage.total_tokens || (prompt + completion);
          this.contextWindowBreakdown.prompt = prompt;
          this.contextWindowBreakdown.completion = completion;
          this.contextWindowBreakdown.reserved = this.maxContextWindow - (prompt + completion);
          this.contextWindowUsed = prompt + completion;
        } else {
          this.contextWindowBreakdown = { prompt: 0, completion: 0, reserved: this.maxContextWindow };
          this.contextWindowUsed = 0;
        }
      }
    } catch (error) {
      this.messages = this.messages.filter(m => !m.isLoading);  
      this.messages.push({
        role: 'assistant',
        content: 'Sorry, something went wrong while processing your request. Please try again, or simplify your query.',
        warnings: ['PROCESSING_ERROR'],
        timestamp: new Date()
      });
    } finally {
      this.isLoading = false;

      this.shouldScrollToBottom = true;
    }
  }

  // ─── FOLLOW-UP CLICK ──────────────────────────────────────────────────────────
  onSuggestionClick(suggestion: string): void {
    this.sendMessage(suggestion);
  }

  // ─── KEYBOARD ─────────────────────────────────────────────────────────────────
  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  // ─── CLEAR CONVERSATION ───────────────────────────────────────────────────────
  clearConversation(): void {
    this.messages = [];
    this.showGreeting();
  }

  // ─── GET TOTAL TOKEN USAGE ────────────────────────────────────────────────────
  getTotalTokenUsage(): number {
    return this.plannerTokenUsed + this.contextWindowUsed;
  }

  // ─── EXPORT CSV ───────────────────────────────────────────────────────────────
  exportChat(): void {
    const rows = this.messages
      .filter(m => !m.isLoading)
      .map(m => `"${m.role}","${new Date(m.timestamp).toLocaleTimeString()}","${m.content.replace(/"/g, '""')}"`);

    const csv = ['Role,Time,Content', ...rows].join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const today = new Date().toISOString().split('T')[0];
    link.download = `smarter-chat-${today}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }



  private scrollToBottom(): void {
    try {
      if (this.messagesContainer) {
        this.messagesContainer.nativeElement.scrollTop =
          this.messagesContainer.nativeElement.scrollHeight;
      }
    } catch {}
  }

  formatTime(date: Date): string {
    return new Date(date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }

  getWarningClass(warning: string): string {
    if (warning.includes('REGULATORY')) return 'warning-regulatory';
    if (warning.includes('PERMISSION')) return 'warning-permission';
    return 'warning-general';
  }

  getWarningIcon(warning: string): string {
    if (warning.includes('REGULATORY')) return '🚨';
    if (warning.includes('PERMISSION')) return '🔒';
    return '⚠️';
  }

  trackByIndex(index: number): number {
    return index;
  }

  /**
   * Auto-detect structured data in text response and convert to table format
   * Detects patterns like:
   * - Facility Name: value
   * - Facility Type: value
   * - Selected Oil: 123.45 BPD
   */
  private enhanceVisualizationFromText(
    answerText: string,
    existingVisualization?: VisualizationPayload
  ): VisualizationPayload | undefined {
    // If backend already provided visualization with data, use it
    if (existingVisualization?.report?.rows && existingVisualization.report.rows.length > 0) {
      return existingVisualization;
    }

    // Try to detect structured key-value data patterns
    const structuredData = this.detectStructuredData(answerText);
    
    if (structuredData.length === 0) {
      return existingVisualization;
    }

    // Convert detected structured data into table format
    const columns = this.extractColumns(structuredData);
    const rows = structuredData;

    return {
      enabled: true,
      intent: 'report',
      reasoning: 'Auto-detected structured data in response - displaying as table for better readability',
      importance: 'medium',
      metadata: {
        generatedAtUtc: new Date().toISOString(),
        sourceTables: existingVisualization?.metadata?.sourceTables || [],
        aggregationDetected: false
      },
      report: {
        title: this.extractTableTitle(answerText) || 'Query Results',
        columns: columns,
        rows: rows
      }
    };
  }

  /**
   * Detect structured data patterns in text
   * Looks for patterns like "- Key: Value" or "Key: Value"
   */
  private detectStructuredData(text: string): Record<string, any>[] {
    const lines = text.split('\n');
    const dataRows: Record<string, any>[] = [];
    let currentRow: Record<string, any> = {};
    let hasValidData = false;

    for (const line of lines) {
      const trimmed = line.trim();
      
      // Skip empty lines and common non-data lines
      if (!trimmed || trimmed.startsWith('Here is') || trimmed.startsWith('💡')) {
        continue;
      }

      // Match patterns like "- Facility Name: VALUE" or "Facility Name: VALUE"
      const keyValueMatch = trimmed.match(/^-?\s*([A-Za-z][A-Za-z\s]+?):\s*(.+)$/);
      
      if (keyValueMatch) {
        const key = keyValueMatch[1].trim();
        let value = keyValueMatch[2].trim();
        
        // Clean up the value - remove units for storage, keep for display
        const numericMatch = value.match(/^([\d,]+\.?\d*)\s*(.*)$/);
        if (numericMatch) {
          const numValue = parseFloat(numericMatch[1].replace(/,/g, ''));
          const unit = numericMatch[2];
          
          // Store the field name and value
          const fieldName = this.toFieldName(key);
          currentRow[fieldName] = numValue;
          currentRow[fieldName + '_display'] = value; // Keep formatted version
          hasValidData = true;
        } else {
          currentRow[this.toFieldName(key)] = value;
          hasValidData = true;
        }
      } else if (hasValidData && Object.keys(currentRow).length > 2) {
        // If we have accumulated data and hit a non-matching line, start new row
        dataRows.push({ ...currentRow });
        currentRow = {};
        hasValidData = false;
      }
    }

    // Add last row if it has data
    if (hasValidData && Object.keys(currentRow).length > 0) {
      dataRows.push(currentRow);
    }

    // Only return if we detected at least 3 fields per row (meaningful table)
    return dataRows.length > 0 && Object.keys(dataRows[0]).length >= 3 ? dataRows : [];
  }

  /**
   * Extract column definitions from detected data rows
   */
  private extractColumns(rows: Record<string, any>[]): any[] {
    if (rows.length === 0) return [];
    
    const firstRow = rows[0];
    const columns: any[] = [];
    
    for (const key of Object.keys(firstRow)) {
      // Skip display fields
      if (key.endsWith('_display')) continue;
      
      const value = firstRow[key];
      const isNumber = typeof value === 'number';
      
      columns.push({
        field: key,
        headerName: this.toHeaderName(key),
        type: isNumber ? 'number' : 'text'
      });
    }
    
    return columns;
  }

  /**
   * Extract table title from text (looks for patterns before the data)
   */
  private extractTableTitle(text: string): string | undefined {
    const lines = text.split('\n');
    for (const line of lines) {
      const trimmed = line.trim();
      // Look for sentences ending with colon or mentioning "summarized", "breakdown", etc.
      if (trimmed.includes('summarized') || trimmed.includes('breakdown') || 
          (trimmed.endsWith(':') && trimmed.length < 100)) {
        return trimmed.replace(/:$/, '');
      }
    }
    return undefined;
  }

  /**
   * Convert display name to field name (e.g., "Facility Name" -> "facilityName")
   */
  private toFieldName(displayName: string): string {
    return displayName
      .split(/\s+/)
      .map((word, index) => {
        word = word.toLowerCase();
        return index === 0 ? word : word.charAt(0).toUpperCase() + word.slice(1);
      })
      .join('');
  }

  /**
   * Convert field name to header name (e.g., "facilityName" -> "Facility Name")
   */
  private toHeaderName(fieldName: string): string {
    return fieldName
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (str) => str.toUpperCase())
      .trim();
  }

  getMessageParts(msg: ChatMessage): MessageContentPart[] {
    if (msg.role !== 'assistant' || msg.isLoading) {
      return [{ type: 'text', text: msg.content || '' }];
    }

    const cachedParts = this.messagePartsCache.get(msg);
    if (cachedParts) {
      return cachedParts;
    }

    const parsedParts = this.parseMessageContent(msg.content || '');
    this.messagePartsCache.set(msg, parsedParts);
    return parsedParts;
  }

  private parseMessageContent(content: string): MessageContentPart[] {
    const normalizedContent = this.normalizeTableMarkup(content);
    const lines = normalizedContent.replace(/\r\n/g, '\n').split('\n');
    const parts: MessageContentPart[] = [];
    let textBuffer: string[] = [];

    const flushText = (): void => {
      if (!textBuffer.length) return;
      const text = textBuffer.join('\n').trim();
      if (text) {
        parts.push({ type: 'text', text });
      }
      textBuffer = [];
    };

    let i = 0;
    while (i < lines.length) {
      let current = lines[i] || '';
      const next = i + 1 < lines.length ? lines[i + 1] : '';
      const nextHasTwoPlusCells = this.hasTwoPlusCells(next);

      // Support inline table header text like:
      // "Here are results: | Col A | Col B |"
      if (!current.trim().startsWith('|') && current.includes('|') && (this.isTableDelimiter(next) || nextHasTwoPlusCells)) {
        const firstPipeIndex = current.indexOf('|');
        const prefixText = current.slice(0, firstPipeIndex).trim();
        const headerCandidate = current.slice(firstPipeIndex).trim();

        if (this.isTableHeader(headerCandidate)) {
          if (prefixText) {
            textBuffer.push(prefixText);
          }
          current = headerCandidate;
        }
      }

      // Markdown table syntax with delimiter row.
      if (this.isTableHeader(current) && this.isTableDelimiter(next)) {
        flushText();

        const headerCells = this.splitTableCells(current);
        const tableRows: string[][] = [];

        i += 2;
        while (i < lines.length && this.looksLikeTableRow(lines[i])) {
          const rowCells = this.splitTableCells(lines[i]);
          if (rowCells.some(cell => cell.length > 0)) {
            tableRows.push(rowCells);
          }
          i += 1;
        }

        if (headerCells.length && tableRows.length) {
          parts.push({
            type: 'table',
            headers: headerCells,
            rows: tableRows
          });
        } else {
          textBuffer.push(current);
          textBuffer.push(next);
        }
        continue;
      }

      // Fallback for plain pipe-delimited rows (2+ columns) without markdown delimiter row.
      if (this.looksLikePipeTableLine(current) && this.looksLikePipeTableLine(next)) {
        flushText();

        const headerCells = this.splitTableCells(current);
        const tableRows: string[][] = [];

        i += 1;
        while (i < lines.length && this.looksLikePipeTableLine(lines[i])) {
          const rowCells = this.splitTableCells(lines[i]);
          if (rowCells.some(cell => cell.length > 0)) {
            tableRows.push(rowCells);
          }
          i += 1;
        }

        if (headerCells.length >= 2 && tableRows.length) {
          parts.push({
            type: 'table',
            headers: headerCells,
            rows: tableRows
          });
        } else {
          textBuffer.push(current);
          for (const row of tableRows) {
            textBuffer.push(`| ${row.join(' | ')} |`);
          }
        }
        continue;
      }

      textBuffer.push(current);
      i += 1;
    }

    flushText();

    return parts.length ? parts : [{ type: 'text', text: normalizedContent }];
  }

  private normalizeTableMarkup(content: string): string {
    let normalized = (content || '').replace(/\r\n/g, '\n');

    // Convert "...: | col1 | col2 |" into a new-line table start.
    normalized = normalized.replace(/:\s+\|/g, ':\n|');

    // Convert compact rows like "| ... | | ... |" into separate lines.
    // This targets the common collapsed-table format from LLM output.
    normalized = normalized.replace(/\|\s+\|/g, '|\n|');

    // Keep spacing tidy after normalization.
    normalized = normalized.replace(/\n{3,}/g, '\n\n').trim();
    return normalized;
  }

  private isTableHeader(line: string): boolean {
    return line.includes('|') && this.splitTableCells(line).length >= 2;
  }

  private isTableDelimiter(line: string): boolean {
    return /^\s*\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|?\s*$/.test(line || '');
  }

  private looksLikeTableRow(line: string): boolean {
    const trimmed = (line || '').trim();
    if (!trimmed || !trimmed.includes('|')) {
      return false;
    }
    return !this.isTableDelimiter(trimmed);
  }

  private splitTableCells(line: string): string[] {
    let normalized = (line || '').trim();
    if (normalized.startsWith('|')) {
      normalized = normalized.slice(1);
    }
    if (normalized.endsWith('|')) {
      normalized = normalized.slice(0, -1);
    }
    return normalized.split('|').map(cell => cell.trim());
  }

  private hasTwoPlusCells(line: string): boolean {
    return this.splitTableCells(line).length >= 2;
  }

  private looksLikePipeTableLine(line: string): boolean {
    const raw = (line || '');
    const trimmed = raw.trim();
    if (!trimmed || !this.hasTwoPlusCells(trimmed)) {
      return false;
    }

    // Reduce false positives like plain text with a single unspaced pipe.
    return (
      trimmed.startsWith('|') ||
      trimmed.endsWith('|') ||
      raw.includes(' | ')
    );
  }

  // ─── PREDEFINED QUESTIONS ─────────────────────────────────────────────────────
  togglePredefinedQuestions(): void {
    this.isPredefinedExpanded = !this.isPredefinedExpanded;
  }

  selectPredefinedQuestion(question: string): void {
    this.userQuery = question;
    this.sendMessage();
  }
}
